/**
 * Jersey Mapping Utilities
 *
 * Utilities for auto-populating jersey images based on team names and player data.
 * Maps team names to CDN jersey URLs with support for blank jerseys and numbered jerseys.
 * Supports both NHL and NFL sports.
 */

import nhlRosterData from '@/data/nhl_rosters.json';
import nflRosterData from '@/data/nfl_rosters.json';

const CDN_BASE_URL = 'https://saracenpickemdev.blob.core.windows.net/jerseys';

export type Sport = 'NHL' | 'NFL';

interface TeamRoster {
  teamName: string;
  players: Array<{
    name: string;
    jerseyNo: number;
    position?: string; // NFL: QB, RB, WR, TE, DE, etc.
  }>;
}

/**
 * Get roster data for a specific sport
 */
function getRosterData(sport: Sport): TeamRoster[] {
  return sport === 'NHL' ? (nhlRosterData as TeamRoster[]) : (nflRosterData as TeamRoster[]);
}

/**
 * Convert team name to jersey CDN format
 * "Boston Bruins" → "Boston_Bruins"
 * "New York Rangers" → "New_York_Rangers"
 */
export function convertTeamNameToJerseyFormat(teamName: string): string {
  return teamName.replace(/\s+/g, '_');
}

/**
 * Generate blank jersey URL for a team
 * @param teamName - Team name (e.g., "Boston Bruins" or "Kansas City Chiefs")
 * @param sport - Sport type (NHL or NFL)
 * @returns URL for blank team jersey
 */
export function generateBlankJerseyUrl(teamName: string, sport: Sport = 'NHL'): string {
  const jerseyTeamName = convertTeamNameToJerseyFormat(teamName);

  if (sport === 'NFL') {
    // NFL jerseys are in Azure CDN under nfl/ subdirectory
    return `${CDN_BASE_URL}/nfl/${jerseyTeamName}.png`;
  }
  // NHL jerseys are in Azure CDN at root level
  return `${CDN_BASE_URL}/${jerseyTeamName}.png`;
}

/**
 * Generate numbered jersey URL for a player
 * @param teamName - Team name (e.g., "Boston Bruins")
 * @param jerseyNo - Jersey number
 * @param sport - Sport type (NHL or NFL)
 * @returns URL for numbered team jersey
 */
export function generatePlayerJerseyUrl(
  teamName: string,
  jerseyNo: number,
  sport: Sport = 'NHL'
): string {
  const jerseyTeamName = convertTeamNameToJerseyFormat(teamName);

  if (sport === 'NFL') {
    // NFL uses the same jersey for all players (no numbers)
    return `${CDN_BASE_URL}/nfl/${jerseyTeamName}.png`;
  }
  // NHL jerseys are in Azure CDN at root level with numbers
  return `${CDN_BASE_URL}/${jerseyTeamName}_${jerseyNo}.png`;
}

/**
 * Lookup player jersey number from roster data
 * @param playerName - Player full name
 * @param teamName - Team name (e.g., "Boston Bruins")
 * @param sport - Sport type (NHL or NFL)
 * @returns Jersey number or null if not found
 */
export function lookupPlayerJerseyNumber(
  playerName: string,
  teamName: string,
  sport: Sport = 'NHL'
): number | null {
  const rosters = getRosterData(sport);
  const roster = rosters.find(r => r.teamName === teamName);

  if (!roster) {
    console.warn(`[jersey-mapping] No roster found for team: ${teamName} in ${sport}`);
    return null;
  }

  const player = roster.players.find(p => p.name === playerName);

  if (!player) {
    console.warn(`[jersey-mapping] Player ${playerName} not found in ${teamName} roster`);
    return null;
  }

  return player.jerseyNo;
}

/**
 * Generate jersey URL for a player by name and team
 * Automatically looks up the player's jersey number
 * @param playerName - Player full name
 * @param teamName - Team name (e.g., "Boston Bruins")
 * @param sport - Sport type (NHL or NFL)
 * @returns CDN URL for player's numbered jersey, or null if player not found
 */
export function generateJerseyUrlForPlayer(
  playerName: string,
  teamName: string,
  sport: Sport = 'NHL'
): string | null {
  const jerseyNo = lookupPlayerJerseyNumber(playerName, teamName, sport);

  if (jerseyNo === null) {
    return null;
  }

  return generatePlayerJerseyUrl(teamName, jerseyNo, sport);
}

/**
 * Check if a team has jersey images available
 * @param teamName - Team name (e.g., "Boston Bruins")
 * @param sport - Sport type (NHL or NFL)
 * @returns true if team exists in roster data
 */
export function hasJerseyData(teamName: string, sport: Sport = 'NHL'): boolean {
  const rosters = getRosterData(sport);
  return rosters.some(r => r.teamName === teamName);
}

/**
 * Get all teams for a specific sport
 * @param sport - Sport type (NHL or NFL)
 * @returns Array of team names
 */
export function getAllTeams(sport: Sport): string[] {
  const rosters = getRosterData(sport);
  return rosters.map(r => r.teamName);
}

/**
 * Get all players for a specific team
 * @param teamName - Team name
 * @param sport - Sport type (NHL or NFL)
 * @returns Array of players or empty array if team not found
 */
export function getTeamPlayers(teamName: string, sport: Sport = 'NHL') {
  const rosters = getRosterData(sport);
  const roster = rosters.find(r => r.teamName === teamName);
  return roster?.players || [];
}
